/**
 * Transaction detail drawer: uses shared DetailDrawer + DetailDrawerContent.
 * Shows transaction fields (id, description, rentId, date, price, type).
 */
import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import dayjs from 'dayjs';
import * as api from '../../modules/api';
import DetailDrawer from '../components/DetailDrawer';
import { Section, Field, CopyableField, CopyableFieldWithAction } from '../components/DetailDrawerContent';
import { isSuperAdmin } from '../utils/accessControl';

const CROSS_LINK_RENT_ID = 'crossLinkRentId';

function formatDate(value) {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) {
    const [y, m, d, h = 0, min = 0, s = 0, nano = 0] = value;
    const ms = typeof nano === 'number' ? Math.round(nano / 1e6) : 0;
    const date = new Date(y, (m || 1) - 1, d || 1, h, min, s, ms);
    return dayjs(date).format('DD.MM.YYYY HH:mm');
  }
  if (typeof value === 'string' && /^\d{4},\d+,\d+/.test(value)) {
    const parts = value.split(',').map(Number);
    const [y, m, d, h = 0, min = 0, s = 0, nano = 0] = parts;
    const ms = nano ? Math.round(nano / 1e6) : 0;
    const date = new Date(y, (m || 1) - 1, d || 1, h, min, s, ms);
    return dayjs(date).format('DD.MM.YYYY HH:mm');
  }
  const d = dayjs(value);
  return d.isValid() ? d.format('DD.MM.YYYY HH:mm') : '—';
}

export default function TransactionDetailDrawer({ transaction, open, onClose, onRefresh, onTransactionUpdated }) {
  const { t } = useTranslation();
  const tx = transaction;
  const canEditAmount = isSuperAdmin();
  const [amountDraft, setAmountDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (!canEditAmount) return;
    if (tx?.price != null && tx.price !== '') setAmountDraft(String(tx.price));
    else setAmountDraft('');
    setSaveError(null);
  }, [tx?.id, tx?.price, canEditAmount]);

  const handleCopy = useCallback((text) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
    }
  }, []);

  const handleOpenRent = useCallback(() => {
    const rentId = tx?.rentId;
    if (!rentId) return;
    try {
      sessionStorage.setItem(CROSS_LINK_RENT_ID, String(rentId));
      window.location.hash = '#/rents';
    } catch (_) {}
  }, [tx?.rentId]);

  const handleSaveAmount = useCallback(() => {
    if (!canEditAmount) return;
    const id = tx?.id ?? tx?._id;
    if (id == null) return;
    const num = amountDraft === '' ? null : Number(amountDraft);
    if (num !== null && !Number.isFinite(num)) {
      setSaveError(t('drawer.transaction.amountError'));
      return;
    }
    setSaveError(null);
    setSaving(true);
    api.editTransaction(id, tx.description ?? '', num ?? 0, tx.rentId ?? '', tx.transactionStatus ?? '')
      .then(() => {
        if (onTransactionUpdated) onTransactionUpdated({ ...tx, price: num });
        if (onRefresh) onRefresh();
      })
      .catch((e) => setSaveError(e?.response?.data?.message || e?.message || t('drawer.transaction.saveFailed')))
      .finally(() => setSaving(false));
  }, [canEditAmount, tx, amountDraft, onRefresh, onTransactionUpdated, t]);

  return (
    <DetailDrawer title={t('drawer.transaction.title')} open={open} onClose={onClose}>
      <Box sx={{ minWidth: 0 }}>
        {!tx ? (
          <Typography color="text.secondary">{t('drawer.transaction.noSelection')}</Typography>
        ) : (
          <Section title={t('drawer.transaction.details')}>
            <CopyableField
              label={t('drawer.transaction.id')}
              value={tx.id ?? tx._id}
              onCopy={handleCopy}
              copyTooltip={t('drawer.copy')}
            />
            <Field label={t('drawer.transaction.description')} value={tx.description} />
            <CopyableFieldWithAction
              label={t('drawer.transaction.rentId')}
              value={tx.rentId}
              onCopy={handleCopy}
              onOpen={handleOpenRent}
              copyTooltip={t('drawer.copy')}
              openTooltip={t('drawer.transaction.openInRents')}
            />
            <Field label={t('drawer.transaction.dateTime')} value={formatDate(tx.createdDate ?? tx.createdAt ?? tx.date)} />
            {canEditAmount ? (
              <Box sx={{ py: 0.75 }}>
                <Typography variant="caption" color="text.secondary" display="block">{t('drawer.transaction.amount')}</Typography>
                <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                    value={amountDraft}
                    onChange={(e) => setAmountDraft(e.target.value)}
                    error={!!saveError}
                    helperText={saveError}
                    sx={{ flex: 1, minWidth: 0 }}
                  />
                  <Button variant="contained" size="small" onClick={handleSaveAmount} disabled={saving}>
                    {saving ? t('drawer.saving') : t('drawer.save')}
                  </Button>
                </Stack>
              </Box>
            ) : (
              <Field label={t('drawer.transaction.amount')} value={tx.price} />
            )}
            <Field label={t('drawer.transaction.type')} value={tx.transactionStatus} />
          </Section>
        )}
      </Box>
    </DetailDrawer>
  );
}
